import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentUrlOutput, routes } from '@gg/shared';
import { api } from '@/lib/api';

type State =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; objectUrl: string; type: string };

/**
 * Inline preview of a certificate for the organiser (A-05). The API signs a GET valid 10 minutes
 * and writes the read to `audit_log`; the file is then fetched into a blob shown in this page, so
 * the signed URL never sits in the address bar or the history, and the preview works the same with
 * R2's presigned URLs and the Worker's (CORS on both).
 */
export function DocumentPreview({
  documentId,
  fileName,
}: {
  documentId: string;
  fileName: string;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const { url } = await api(routes.adminDocumentUrl(documentId), {
        schema: documentUrlOutput,
      });
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setState({ status: 'ready', objectUrl, type: blob.type });
    })().catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  if (state.status === 'loading') {
    return (
      <div className="flex h-[70vh] items-center justify-center rounded-control bg-surface text-sm text-ink-muted">
        {t('admin.review.preview.loading')}
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <p role="alert" className="rounded-control bg-surface p-4 text-sm text-danger">
        {t('admin.review.preview.error')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {state.type.startsWith('image/') ? (
        <img
          src={state.objectUrl}
          alt={fileName}
          className="max-h-[70vh] w-full rounded-control border border-line bg-surface object-contain"
        />
      ) : (
        <iframe
          src={state.objectUrl}
          title={fileName}
          className="h-[70vh] w-full rounded-control border border-line bg-surface"
        />
      )}
      <a
        href={state.objectUrl}
        target="_blank"
        rel="noreferrer"
        download={fileName}
        className="self-start text-sm font-semibold"
      >
        {t('admin.review.preview.open')}
      </a>
    </div>
  );
}
